import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router";
import { ForgotPasswordInitForm } from "../../components/forms/forgotPasswordInitForm";
import { useClient } from "../../providers/supabase";
import { useAsyncAction } from "../../hooks/useAsyncAction";
import { present } from "../../../lib/util/present";
import { Session, VerifyTokenHashParams } from "@supabase/supabase-js";
import { useSignInContext } from "./context";
import { useAsyncEffect } from "../../hooks/useAsyncEffect";

export function ForgotPassword() {
  const client = useClient()
  const [emailSent, setEmailSent] = useState('')
  const {onLoginSuccess} = useSignInContext()

  const [search, _] = useSearchParams()
  const tokenHash = search.get('token_hash')

  const {loading, error} = useAsyncEffect(async () => {
    if (tokenHash) {
      const params: VerifyTokenHashParams = {
        token_hash: tokenHash,
        type: 'recovery'
      }
      let { data, error } = await client.auth.verifyOtp(params)
      if (error) {
        throw error
      }
      if (data.session) {
        onLoginSuccess({ session: data.session }, '/changePassword')
      } else {
        throw new Error('Unable to verify your token. Please contact us for help.')
      }
    }
  }, [])

  return <div className='row mt-4'>
    <div className='col col-lg-6 offset-lg-3'>
      <h1>Reset My Password</h1>
      <p>Enter your email below and we'll send you a code to reset your password</p>
      {!emailSent &&
        <ForgotPasswordInitForm onEmailSent={(email) => setEmailSent(email)} />}
      {emailSent &&
        <ForgotPasswordVerifyForm email={emailSent} onLoginSuccess={(data) => { onLoginSuccess?.(data, '/changePassword') }} />}
    </div>
  </div>
}

export function ForgotPasswordVerifyForm({email, onLoginSuccess}: {email: string, onLoginSuccess: (data: {session: Session}) => void}) {
  const client = useClient()
  const navigate = useNavigate()


  const [token, setToken] = useState('')
  const canSubmit = present(token)

  const [submitState, submit] = useAsyncAction(async () => {
    const { data, error } = await client.auth.verifyOtp({
      email: email,
      token: token,
      type: 'recovery'
    })

    if (error) { 
      throw new Error('Unable to verify your code. Please contact us for help.', {
        cause: error,
      });
    }
    const session = data.session
    if (!session) { throw new Error('Unable to verify your code. Please contact us for help.') }
    navigate(`/changePassword`)
    onLoginSuccess({session})
  }, [email, token, navigate, onLoginSuccess])

  return <form onSubmit={(e) => { e.preventDefault(); submit() }}>
    <div className="mb-3">
      <span className='form-label'>Email</span>
      <input name="email" type="text"
        className="form-control"
        required
        disabled={true}
        value={email}></input>
    </div>

    <p>
      Please check your email and click on the link to reset your password.
    </p>
    <p>
      If that doesn't work, you can enter the 6-digit alternate code below.
    </p>
    <div className="mb-3">
    <input type="text" className='form-control'
          placeholder="6-digit code"
          value={token}
          onChange={(e) => {
            setToken(e.target.value)
          }} />
      </div>

    <div className="mb-3">
      {submitState.loading ?
        <button className='btn btn-primary disabled'>
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </button> :
        <button type='submit' className='btn btn-primary' disabled={!canSubmit}>Submit</button>}
    </div>

    <div className="mb-3">
      {submitState.error &&
        <p className="text-danger">
          {submitState.error.message}
        </p>}
    </div>
  </form>
}
